import React from 'react';
import { createCurrencyFormatter } from '../../utils/globalCurrency';

interface TripleColumnItem {
  id: number | string;
  date: string;
  issueQuantity?: number;
  issuePrice?: number;
  issueAmount?: number;
  receiptQuantity?: number;
  receiptPrice?: number;
  receiptAmount?: number;
  balanceQuantity?: number;
  balancePrice?: number;
  balanceAmount?: number;
}

interface StockValuationPrintableProps {
  title: string;
  items: TripleColumnItem[];
  startDate?: string;
  endDate?: string;
  currencySymbol?: string;
}

// Create currency formatter with default $ symbol
const createFormatCurrency = (currencySymbol: string = '$') => {
  const formatter = createCurrencyFormatter(currencySymbol);
  return (amount: number) => formatter.format(amount, 2);
};

const formatNumber = (amount: number, isQuantity: boolean = false) =>
  amount.toLocaleString('en-US', {
    minimumFractionDigits: isQuantity ? 0 : 2,
    maximumFractionDigits: isQuantity ? 0 : 2,
  });

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const displayValue = (value: number | undefined, isCurrency = false, formatCurrency?: (amount: number) => string, isQuantity = false) => {
  if (value === undefined || value === 0) return '';
  if (isCurrency && formatCurrency) {
    return formatCurrency(value);
  }
  return isCurrency ? '' : formatNumber(value, isQuantity);
};

const StockValuationPrintable = React.forwardRef<HTMLDivElement, StockValuationPrintableProps>(
  ({ title, items, startDate, endDate, currencySymbol = '$' }, ref) => {
    // Create currency formatter with provided symbol
    const formatCurrency = createFormatCurrency(currencySymbol);
    const totalIssueQty = items.reduce((s, i) => s + (i.issueQuantity || 0), 0);
    const totalIssueAmt = items.reduce((s, i) => s + (i.issueAmount || 0), 0);
    const totalReceiptQty = items.reduce((s, i) => s + (i.receiptQuantity || 0), 0);
    const totalReceiptAmt = items.reduce((s, i) => s + (i.receiptAmount || 0), 0);
    const netMovement = Math.abs(totalIssueAmt - totalReceiptAmt);
    const isNetIssue = totalIssueAmt >= totalReceiptAmt;

    const cellStyle: React.CSSProperties = {
      padding: '6px 8px',
      fontSize: '12px',
      borderBottom: '1px solid #e5e7eb',
    };

    const headerCellStyle: React.CSSProperties = {
      ...cellStyle,
      fontWeight: 600,
      color: '#6b7280',
      borderBottom: '2px solid #d1d5db',
    };

    return (
      <div
        ref={ref}
        style={{
          width: '800px',
          background: '#ffffff',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#1f2937',
          padding: '32px',
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>{title}</div>
          {(startDate || endDate) && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {startDate && `From: ${startDate}`}
              {startDate && endDate && '  —  '}
              {endDate && `To: ${endDate}`}
            </div>
          )}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, textAlign: 'left', width: '12%' }}>Date</th>
              <th style={{ ...headerCellStyle, textAlign: 'center', borderLeft: '1px solid #d1d5db' }} colSpan={3}>
                Issue
              </th>
              <th style={{ ...headerCellStyle, textAlign: 'center', borderLeft: '1px solid #d1d5db' }} colSpan={3}>
                Receipt
              </th>
            </tr>
            <tr>
              <th style={{ ...headerCellStyle }}></th>
              <th style={{ ...headerCellStyle, textAlign: 'right', borderLeft: '1px solid #d1d5db' }}>Qty</th>
              <th style={{ ...headerCellStyle, textAlign: 'right' }}>Price</th>
              <th style={{ ...headerCellStyle, textAlign: 'right' }}>Amt</th>
              <th style={{ ...headerCellStyle, textAlign: 'right', borderLeft: '1px solid #d1d5db' }}>Qty</th>
              <th style={{ ...headerCellStyle, textAlign: 'right' }}>Price</th>
              <th style={{ ...headerCellStyle, textAlign: 'right' }}>Amt</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td style={{ ...cellStyle, color: '#6b7280' }}>{formatDate(item.date)}</td>
                <td style={{ ...cellStyle, textAlign: 'right', borderLeft: '1px solid #e5e7eb' }}>
                  {displayValue(item.issueQuantity, false, undefined, true)}
                </td>
                                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {displayValue(item.issuePrice, true, formatCurrency)}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right', color: item.issueAmount ? '#dc2626' : undefined }}>
                  {displayValue(item.issueAmount, true, formatCurrency)}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right', borderLeft: '1px solid #e5e7eb' }}>
                  {displayValue(item.receiptQuantity, false, undefined, true)}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {displayValue(item.receiptPrice, true, formatCurrency)}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right', color: item.receiptAmount ? '#16a34a' : undefined }}>
                  {displayValue(item.receiptAmount, true, formatCurrency)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #d1d5db' }}>
              <td style={{ ...cellStyle, fontWeight: 600, textAlign: 'right' }}>Total:</td>
              <td style={{ ...cellStyle, fontWeight: 600, textAlign: 'right', color: '#dc2626', borderLeft: '1px solid #e5e7eb' }}>
                {formatNumber(totalIssueQty, true)}
              </td>
              <td style={{ ...cellStyle }}></td>
              <td style={{ ...cellStyle, fontWeight: 600, textAlign: 'right', color: '#dc2626' }}>
                {formatCurrency(totalIssueAmt)}
              </td>
              <td style={{ ...cellStyle, fontWeight: 600, textAlign: 'right', color: '#16a34a', borderLeft: '1px solid #e5e7eb' }}>
                {formatNumber(totalReceiptQty, true)}
              </td>
              <td style={{ ...cellStyle }}></td>
              <td style={{ ...cellStyle, fontWeight: 600, textAlign: 'right', color: '#16a34a' }}>
                {formatCurrency(totalReceiptAmt)}
              </td>
            </tr>
            <tr>
              <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>
                Net Movement:
              </td>
              <td
                colSpan={3}
                style={{
                  ...cellStyle,
                  textAlign: 'right',
                  fontWeight: 600,
                  color: isNetIssue ? '#dc2626' : '#16a34a',
                }}
              >
                {formatCurrency(netMovement)} {isNetIssue ? '(Net Issue)' : '(Net Receipt)'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }
);

StockValuationPrintable.displayName = 'StockValuationPrintable';

export default StockValuationPrintable;
